import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { CircularProgress } from '@mui/material';

import { AdminDashboardApi } from '@app/domain/dashboard/api/AdminDashboardApi';
import { clsxm } from '@app/styles/clsxm';

import { DashboardCard } from '../shared/DashboardCard';
import { useChartTable } from '../shared/useChartTable';
import { DASHBOARD_COLOR } from '../shared/dashboardColors';

const fmt = (n: number) => n.toLocaleString('nl-NL');
const weekLabel = (iso: string) => format(parseISO(iso), 'd MMM', { locale: nl });

// admin-dashboard-opdracht.md §4.3 (band 1b). is_deleted=false is enforced server-side
// (QRCodeRepository.countCreatedPerWeek/countCreatedPerType's own javadoc) - nothing to repeat
// here, just display what comes back.
export const DashboardQrCodesBand: React.FC<{ from: string; to: string }> = ({ from, to }) => {
  const query = useQuery(['admin-dashboard-qr-codes', from, to], () => AdminDashboardApi.getQrCodes(from, to));
  const weeks = useChartTable();

  if (query.isLoading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (!query.data) {
    return null;
  }

  const { perWeek, perType } = query.data;
  const totalTypes = perType.reduce((a, r) => a + r.count, 0);
  const maxType = Math.max(1, ...perType.map((r) => r.count));

  return (
    <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
      <DashboardCard
        title="Aangemaakte QR-codes"
        subtitle="Per week - verwijderde codes niet meegeteld"
        headerRight={weeks.toggleButton}
      >
        {weeks.showTable ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-gray-400">
                  <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Week</th>
                  <th className="py-2 text-right font-semibold uppercase tracking-wide">Aangemaakte codes</th>
                </tr>
              </thead>
              <tbody>
                {perWeek.map((r, i) => (
                  <tr key={r.weekStart} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
                    <td className="py-1.5 pr-3">
                      {weekLabel(r.weekStart)}
                      {r.running && <span className="ml-1.5 text-gray-400">(loopt nog)</span>}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{fmt(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perWeek} margin={{ top: 8, right: 8 }}>
                <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                <XAxis
                  dataKey="weekStart"
                  tickFormatter={weekLabel}
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  stroke={DASHBOARD_COLOR.muted}
                />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} width={32} />
                <RTooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }
                    const row = payload[0].payload as typeof perWeek[number];
                    return (
                      <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] text-white">
                        <b>{weekLabel(label as string)}</b>
                        {row.running && <span className="opacity-70"> (loopt nog)</span>}
                        <br />
                        Aangemaakt: <b>{fmt(row.value)}</b>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {perWeek.map((r) => (
                    <Cell key={r.weekStart} fill={r.running ? DASHBOARD_COLOR.running : DASHBOARD_COLOR.cat1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </DashboardCard>

      <DashboardCard title="Per type" subtitle="Aangemaakt in de gekozen periode">
        <div className="mt-0.5">
          {perType.map((row) => (
            <div key={row.label} className="mb-2.5">
              <div className="mb-1 flex items-center justify-between text-[13px]">
                <b className="text-gray-800">{row.label}</b>
                <span className="text-[12.5px] text-gray-400">
                  {fmt(row.count)} · {row.percent.toFixed(1)}%
                </span>
              </div>
              <div className="h-[13px] overflow-hidden rounded bg-[#eef1f5]">
                <div
                  className="h-full rounded"
                  style={{ width: `${(row.count / maxType) * 100}%`, background: DASHBOARD_COLOR.cat2 }}
                />
              </div>
            </div>
          ))}
          {!perType.length && <p className="py-4 text-center text-gray-400">Geen codes in deze periode.</p>}
        </div>
        <p className="mt-2 text-[11.5px] text-gray-400">
          Eén kleur, want de naam draagt hier de identiteit. Totaal {fmt(totalTypes)} codes in deze periode.
        </p>
      </DashboardCard>
    </div>
  );
};
